import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forOwn } from 'lodash-es';

import {
  HttpConflictError,
  HttpMiscellaneousError,
  HttpNoConnectionError,
  HttpNotFoundError,
  HttpPaymentThreeDSRequired,
  HttpServerError,
  HttpUnprocessableEntityError,
  HttpAlreadyPurchaseError,
} from '@nibtravel/shared/util/models';
import { IValidation } from '@nibtravel/shared/util/models';
import { NibTravelHttpError } from '@nibtravel/shared/util/models';

/**
 * A service that aggregates all usage and messages in regards to errors across the apps.
 *
 * @export
 * @class ErrorService
 */
@Injectable()
export class ErrorService {
  /** Gets an specific HttpError based on the HttpResponse */
  getErrorModel(response?: HttpErrorResponse): NibTravelHttpError {
    if (!response) {
      return new HttpMiscellaneousError();
    }
    switch (response.status) {
      case 0:
        return new HttpNoConnectionError(response);
      case HttpStatusCode.NotFound:
        return new HttpNotFoundError(response);
      case HttpStatusCode.Conflict:
        return new HttpConflictError(response);
      case HttpStatusCode.Gone:
        return new HttpAlreadyPurchaseError(response);
      case HttpStatusCode.PaymentRequired:
        return new HttpPaymentThreeDSRequired(response);
      case HttpStatusCode.UnprocessableEntity: {
        const validation = this.getValidation(response?.error, true);
        if (validation) {
          return new HttpUnprocessableEntityError(response, validation);
        }
      }
    }
    if (response.status >= 500) {
      return new HttpServerError(response);
    }
    return new HttpMiscellaneousError(response);
  }

  /** Gets the validation messages from an specific error type */
  getValidation(obj: any, checkSelf = false): IValidation | undefined {
    let validation: IValidation | undefined;
    if (!obj) {
      return undefined;
    }
    if (
      checkSelf &&
      obj.validation &&
      !obj.validation.isValid &&
      obj.validation.errors
    ) {
      validation = this.mergeValidation(validation, obj.validation);
    }
    if (!(obj instanceof Object)) {
      return validation;
    }
    forOwn(obj, (child) => {
      if (
        child.validation &&
        !child.validation.isValid &&
        child.validation.errors
      ) {
        validation = this.mergeValidation(validation, child.validation);
      }
    });
    forOwn(obj, (child) => {
      validation = this.mergeValidation(validation, this.getValidation(child));
    });
    return validation;
  }

  /** Merge the validation messages */
  private mergeValidation(
    a: IValidation | undefined,
    b: IValidation | undefined
  ): IValidation | undefined {
    if (!a || !b) {
      return a || b;
    }
    return {
      ...a,
      ...b,
      errors: [...((a && a.errors) || []), ...((b && b.errors) || [])],
    };
  }
}
